// OnCatch Widget — entry point.
// Pattern: <script src="...w-1.0.0.js" data-workspace="ws_abc123" data-api="https://admin.oncatch.app" async defer></script>
//
// Ring-buffer console + network interceptors are installed inside bootstrap() before
// UI mount, so submissions always carry recent context.

import { bootstrap } from './bootstrap.ts';
import { resolveConfig } from './config.ts';

// document.currentScript is null for ES module scripts (browser spec), so we can't rely
// on it in dev where Vite serves type="module". In production the built bundle is an IIFE
// where currentScript works. To cover both: locate the script tag by its data-workspace
// attribute, which every integration MUST include anyway.
function findOwnScript(): HTMLScriptElement | null {
  const explicit = document.currentScript as HTMLScriptElement | null;
  if (explicit?.dataset.workspace) return explicit;
  // Fallback: first script tag with data-workspace (the integration snippet)
  return document.querySelector<HTMLScriptElement>('script[data-workspace]');
}

interface FontDeps {
  /** Defaults to the global FontFace. Injectable for tests. */
  FontFaceCtor?: typeof FontFace;
  /** Defaults to `document`. Injectable for tests. */
  doc?: Document;
  /** Defaults to the global fetch. Injectable for tests. (#799) */
  fetchFn?: typeof fetch;
}

type FontHostDoc = Document & { __oncatchFontLoaded?: boolean };

// mode:'cors' — a blocked/misconfigured cross-origin request must reject, not
// resolve to an opaque empty body (which 'no-cors' would do silently: no
// error, but the font never actually loads). credentials:'omit' — the CDN
// answers with Access-Control-Allow-Origin: *, which is invalid to pair with
// credentialed requests.
const FONT_FETCH_INIT: RequestInit = { mode: 'cors', credentials: 'omit' };

/**
 * #799: download one weight's woff2 bytes and build a FontFace from them.
 * `async` on purpose: every throw inside — including a synchronous one from
 * fetchFn or the FontFace constructor — becomes a rejection of the returned
 * promise, so registerWidgetFont's single .catch() is the one terminal for
 * all of them.
 */
async function fetchFontFace(
  FontFaceCtor: typeof FontFace,
  fetchFn: typeof fetch,
  url: string,
  weight: '500' | '600',
): Promise<FontFace> {
  const res = await fetchFn(url, FONT_FETCH_INIT);
  if (!res.ok) throw new Error(`font fetch failed: ${res.status}`);
  const bytes = await res.arrayBuffer();
  const face = new FontFaceCtor('OnCatchUiFont', bytes, {
    weight,
    style: 'normal',
    display: 'swap',
  });
  return face.load();
}

/**
 * #343 / #484 / #799: register Inter via `document.fonts` (the only font
 * registry there is — a ShadowRoot has no `fonts` set of its own) and fetch
 * both woff2 weights immediately so the font swap happens as early as
 * possible. URL is derived from the widget script's own src (prod →
 * widget.oncatch.app, dev → localhost). #343: an `@font-face` CSS rule
 * declared in the shadow root's own stylesheet never triggers a load in
 * Chrome, which is why this goes through `doc.fonts.add()` from here instead.
 * Idempotent if the snippet is double-installed.
 *
 * #799: the font is now fetched as bytes (governed by `connect-src`, which
 * every client already has to allow for the widget to function at all — see
 * docs/embedding-csp.md 5) and built via `new FontFace(family, ArrayBuffer,
 * descriptors)`, instead of the previous `FontFace(family, 'url(...)')`
 * (governed by `font-src`). A host CSP that doesn't allowlist
 * widget.oncatch.app under `font-src` no longer matters.
 *
 * Every failure — a rejected fetch (CSP-blocked, offline, DNS), a non-OK HTTP
 * status, a failed body read, a FontFace constructor/load() rejection — MUST
 * be caught here. An uncaught rejection would surface as an
 * `unhandledrejection` on the host page (and our own installErrorHandlers
 * would relay it as a spurious widget error). A blocked or failed font is
 * purely cosmetic: buildStyles()'s font-family already falls back to
 * system-ui. Synchronous setup errors (FontFace/fetch API missing, bad URL)
 * are swallowed by the outer try/catch for the same reason.
 *
 * Both weights are fetched in parallel via Promise.all, and — matching prior
 * behavior — if either weight fails, NEITHER is registered: a half-branded
 * panel (one Inter weight next to a system-font bold) read as visibly wrong
 * during the original #343 rollout, so a consistent system-font fallback is
 * preferred over a partial swap.
 *
 * The idempotency flag is set, and both fetches are issued, synchronously
 * before this function returns — i.e. before the call site's
 * `queueMicrotask(bootstrap)` runs. bootstrap() installs the fetch-patching
 * network ring-buffer that ships with every submission; a font fetch issued
 * after that point would get misattributed as customer-page network activity
 * in bug reports.
 *
 * fetchFontFace always calls `fetchFn` as a bare function (`fetchFn(url,
 * init)`, never off an object or via .call/.apply) — Chrome throws
 * `TypeError: Illegal invocation` synchronously for a native `fetch`
 * reference invoked method-style. The default wraps the global `fetch` in an
 * arrow function as defence-in-depth for that path specifically; an injected
 * test fetchFn is still expected to tolerate a bare call the same way.
 *
 * Returns the (already-caught) load promise so the #484/#799 regression
 * tests can deterministically assert it RESOLVES rather than rejecting. The
 * production call site fires-and-forgets via `void registerWidgetFont(...)`.
 *
 * Exported for the #484/#799 regression tests.
 */
export function registerWidgetFont(scriptSrc: string, deps: FontDeps = {}): Promise<void> {
  const doc = (deps.doc ?? document) as FontHostDoc;
  const FontFaceCtor =
    deps.FontFaceCtor ?? (typeof FontFace !== 'undefined' ? FontFace : undefined);
  const fetchFn =
    deps.fetchFn ??
    (typeof fetch !== 'undefined'
      ? (input: RequestInfo | URL, init?: RequestInit) => fetch(input, init)
      : undefined);
  try {
    if (!scriptSrc || !FontFaceCtor || !fetchFn || doc.__oncatchFontLoaded) {
      return Promise.resolve();
    }
    const url500 = new URL('/inter-500.woff2', scriptSrc).href;
    const url600 = new URL('/inter-600.woff2', scriptSrc).href;
    // Set before either fetch is issued: two synchronous double-installs must
    // both see the flag, not race each other into fetching twice.
    doc.__oncatchFontLoaded = true;
    return Promise.all([
      fetchFontFace(FontFaceCtor, fetchFn, url500, '500'),
      fetchFontFace(FontFaceCtor, fetchFn, url600, '600'),
    ])
      .then(([f500, f600]) => {
        doc.fonts.add(f500);
        doc.fonts.add(f600);
      })
      .catch(() => {
        // Font fetch/build failed (CSP-blocked, offline, bad response, decode
        // failure) → system-ui fallback renders. Not a blocker. Resolving
        // (via this catch) is what prevents an unhandledrejection on the
        // host page.
      });
  } catch {
    // FontFace/fetch API unavailable, or bad URL → widget falls back to
    // system-ui via the existing font-family stack. Not a blocker.
    return Promise.resolve();
  }
}

const currentScript = findOwnScript();
const config = currentScript ? resolveConfig(currentScript) : null;

if (!config) {
  // biome-ignore lint/suspicious/noConsole: widget boot diagnostic; only way to surface at load time
  console.warn(
    '[OnCatch] missing data-workspace on <script> tag. Widget will not load. ' +
      'Example: <script src="w-1.0.0.js" data-workspace="ws_abc123" data-api="https://admin.oncatch.app" defer></script>',
  );
} else {
  // #343 / #484 / #799: preload Inter (best-effort; gracefully swallowed if
  // the fetch fails for any reason — see registerWidgetFont). Fire-and-forget:
  // the returned promise is always already-resolved (the .catch() is internal).
  if (currentScript?.src) void registerWidgetFont(currentScript.src);
  // Defer bootstrap so we don't block the host page's critical render.
  queueMicrotask(() => {
    void bootstrap(
      config.workspaceId,
      config.apiBaseUrl,
      config.locale,
      config.widgetBaseUrl,
      config.assistantEndpoint,
    );
  });
}
